import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Run an async loader and expose { data, error, loading, reload }.
 *
 *   const { data, error, loading, reload } = useAsyncData((signal) => listAllProjects({ signal }), []);
 *
 * - Re-runs when `deps` change (like useEffect), or on `reload()` (use for "Try again").
 * - Only the latest call may update state; the previous in-flight request is aborted.
 * - Aborts on unmount and never sets state afterwards.
 * - `enabled: false` skips loading (e.g. while waiting for a route param).
 */
export function useAsyncData(loader, deps = [], { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: enabled });
  const controllerRef = useRef(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const run = useCallback(async () => {
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await loaderRef.current(controller.signal);
      if (!controller.signal.aborted) setState({ data, error: null, loading: false });
    } catch (error) {
      if (!controller.signal.aborted) setState((s) => ({ data: s.data, error, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, loading: false }));
      return undefined;
    }
    run();
    return () => {
      if (controllerRef.current) controllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  return { ...state, reload: run };
}
